/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Agent Executions API Client (SkillShop server)
 *
 * Lists agent executions for the current user (sidebar/chat runs, grooming, code gen, etc.)
 * for the Velocity Runs tab and button state awareness.
 */

import type { AgentExecution } from '@shared/schema/agents';
import { authenticatedFetch } from '@shared/lib/api';
import { handleApiResponse } from '@shared/lib/apiResponse';

export type { AgentExecution };

export interface ListAgentExecutionsOptions {
  limit?: number;
  offset?: number;
  /** Single status or comma-separated (e.g. 'pending,running') */
  status?: string;
}

export interface ListAgentExecutionsResult {
  success: boolean;
  executions?: AgentExecution[];
  error?: string;
}

/**
 * List agent executions for the current user (SkillShop server, port 5173).
 * Used by Velocity Runs tab and by Issues/User Stories for button state.
 */
export async function listAgentExecutions(
  options?: ListAgentExecutionsOptions
): Promise<ListAgentExecutionsResult> {
  try {
    const params = new URLSearchParams();
    if (options?.limit != null) params.set('limit', String(options.limit));
    if (options?.offset != null) params.set('offset', String(options.offset));
    if (options?.status) params.set('status', options.status);

    const query = params.toString();
    const url = query ? `/api/agent/executions?${query}` : '/api/agent/executions';
    const response = await authenticatedFetch(url);
    const data = await handleApiResponse<{ executions: AgentExecution[] }>(response);
    return { success: true, executions: data.executions ?? [] };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to list agent executions';
    return { success: false, error: message };
  }
}

export interface CreateVelocityCodeGenExecutionOptions {
  issueId?: string;
  releaseId?: string;
  productId?: string;
  message: string;
}

export interface CreateAgentExecutionResult {
  success: boolean;
  executionId?: string;
  error?: string;
}

/**
 * Create a velocity_code_gen agent execution for run tracking (Runs tab, button state).
 * Call before invoking code_agent; then call updateAgentExecutionStatus on completion/failure.
 */
export async function createVelocityCodeGenExecution(
  options: CreateVelocityCodeGenExecutionOptions
): Promise<CreateAgentExecutionResult> {
  try {
    const metadata: Record<string, unknown> = {
      entityType: options.issueId ? 'issue' : 'release',
      entityId: options.issueId ?? options.releaseId ?? '',
    };
    if (options.productId) metadata.productId = options.productId;
    if (options.issueId) metadata.issueId = options.issueId;
    if (options.releaseId) metadata.releaseId = options.releaseId;

    const response = await authenticatedFetch('/api/agent/executions', {
      method: 'POST',
      body: {
        agentName: 'velocity_code_gen',
        message: options.message,
        metadata,
      },
    });
    const data = await handleApiResponse<{ executionId: string }>(response);
    return { success: true, executionId: data.executionId };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to create execution';
    return { success: false, error: message };
  }
}

export interface UpdateAgentExecutionStatusOptions {
  status: 'completed' | 'failed';
  result?: Record<string, unknown>;
}

/**
 * Update an agent execution after code_agent (or similar) completes.
 */
export async function updateAgentExecutionStatus(
  executionId: string,
  options: UpdateAgentExecutionStatusOptions
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await authenticatedFetch(`/api/agent/executions/${encodeURIComponent(executionId)}`, {
      method: 'PATCH',
      body: {
        status: options.status,
        progress: 100,
        currentStep: options.status === 'completed' ? 'Completed' : 'Failed',
        completedAt: new Date().toISOString(),
        result: options.result ?? null,
      },
    });
    await handleApiResponse(response);
    return { success: true };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to update execution';
    return { success: false, error: message };
  }
}

/**
 * Normalized run for the Runs tab UI.
 * Merges system agent runs and agent executions (and later DSA, grooming, code gen) into one shape.
 */
export type UnifiedRunType =
  | 'system_agent'
  | 'agent_execution'
  | 'grooming'
  | 'code_generation'
  | 'quick_request'
  | 'dsa';

export interface UnifiedRun {
  id: string;
  runType: UnifiedRunType;
  name: string;
  status: string;
  startedAt: string | Date | null;
  completedAt: string | Date | null;
  durationSeconds: number | null;
  result?: string | null;
  summary?: string | null;
  errorMessage?: string | null;
  triggeredBy?: string | null;
  message?: string | null;
  sessionId?: string | null;
  executionId?: string | null;
  agentId?: number | null;
  agentName?: string | null;
  metadata?: Record<string, unknown> | null;
}

/**
 * Map a SystemAgentRun to UnifiedRun for the Runs tab.
 */
export function systemAgentRunToUnifiedRun(
  run: { id: number; runId: string; agentId: number; status: string; startedAt?: Date | string | null; completedAt?: Date | string | null; durationSeconds?: number | null; result?: string | null; summary?: string | null; errorMessage?: string | null; triggeredBy?: string | null },
  agentName: string
): UnifiedRun {
  const started = run.startedAt ? new Date(run.startedAt) : null;
  const completed = run.completedAt ? new Date(run.completedAt) : null;
  return {
    id: run.runId ?? `system-${run.id}`,
    runType: 'system_agent',
    name: agentName,
    status: run.status,
    startedAt: started ?? null,
    completedAt: completed ?? null,
    durationSeconds: run.durationSeconds ?? null,
    result: run.result ?? null,
    summary: run.summary ?? null,
    errorMessage: run.errorMessage ?? null,
    triggeredBy: run.triggeredBy ?? null,
    message: null,
    sessionId: null,
    executionId: null,
    agentId: run.agentId,
    agentName: null,
    metadata: { systemRunId: run.id },
  };
}

/**
 * Map an AgentExecution to UnifiedRun for the Runs tab.
 */
export function agentExecutionToUnifiedRun(exec: AgentExecution): UnifiedRun {
  const started = exec.startedAt ?? exec.createdAt;
  const completed = exec.completedAt;
  const durationSeconds =
    started && completed
      ? Math.round((new Date(completed).getTime() - new Date(started).getTime()) / 1000)
      : null;

  let runType: UnifiedRunType = 'agent_execution';
  const agentName = (exec.agentName ?? '').toLowerCase();
  if (agentName.includes('velocity_grooming') || agentName === 'velocity_grooming') runType = 'grooming';
  else if (agentName.includes('velocity_code_gen') || agentName === 'velocity_code_gen') runType = 'code_generation';
  else if (agentName.includes('velocity_quick_request') || agentName === 'velocity_quick_request') runType = 'quick_request';

  let name = exec.agentName ?? `Agent ${exec.agentId ?? 'unknown'}`;
  if (runType === 'grooming' && exec.metadata && typeof exec.metadata === 'object') {
    const m = exec.metadata as Record<string, unknown>;
    if (m.storyId) name = `Groom story ${String(m.storyId)}`;
    else if (m.epicId) name = `Groom epic ${String(m.epicId)}`;
    else if (m.issueId) name = `Groom issue ${String(m.issueId)}`;
  } else if (runType === 'code_generation' && exec.metadata && typeof exec.metadata === 'object') {
    const m = exec.metadata as Record<string, unknown>;
    if (m.issueId) name = `Code gen ${String(m.issueId)}`;
  }

  return {
    id: exec.executionId ?? exec.id,
    runType,
    name,
    status: exec.status,
    startedAt: started ?? null,
    completedAt: completed ?? null,
    durationSeconds,
    result: exec.result && typeof exec.result === 'object' && 'error' in exec.result ? String((exec.result as { error?: string }).error) : null,
    summary: null,
    errorMessage: exec.result && typeof exec.result === 'object' && 'error' in exec.result ? String((exec.result as { error?: string }).error) : null,
    triggeredBy: null,
    message: exec.message ?? null,
    sessionId: exec.sessionId ?? null,
    executionId: exec.executionId ?? exec.id,
    agentId: exec.agentId ?? null,
    agentName: exec.agentName ?? null,
    metadata: exec.metadata && typeof exec.metadata === 'object' ? (exec.metadata as Record<string, unknown>) : null,
  };
}

/**
 * Map a DSA run to UnifiedRun for the Runs tab.
 */
export function dsaRunToUnifiedRun(run: {
  runId: string;
  agentName: string;
  status: string;
  startedAt: string;
  completedAt?: string;
  duration?: string;
  result?: string;
  resultDescription?: string;
  errorMessage?: string;
  reportPath?: string;
}): UnifiedRun {
  const started = run.startedAt ? new Date(run.startedAt) : null;
  const completed = run.completedAt ? new Date(run.completedAt) : null;
  const durationSeconds = run.duration ? parseDSADurationToSeconds(run.duration) : (started && completed ? Math.round((completed.getTime() - started.getTime()) / 1000) : null);
  return {
    id: run.runId,
    runType: 'dsa',
    name: `DSA: ${run.agentName}`,
    status: run.status,
    startedAt: started ?? null,
    completedAt: completed ?? null,
    durationSeconds,
    result: run.result ?? null,
    summary: run.resultDescription ?? null,
    errorMessage: run.errorMessage ?? null,
    triggeredBy: null,
    message: null,
    sessionId: null,
    executionId: null,
    agentId: null,
    agentName: run.agentName,
    metadata: run.reportPath ? { reportPath: run.reportPath } : null,
  };
}

function parseDSADurationToSeconds(duration: string): number | null {
  if (!duration || typeof duration !== 'string') return null;
  const h = duration.match(/(\d+)\s*h/);
  const m = duration.match(/(\d+)\s*m/);
  const s = duration.match(/(\d+)\s*s/);
  const hours = h ? parseInt(h[1], 10) : 0;
  const mins = m ? parseInt(m[1], 10) : 0;
  const secs = s ? parseInt(s[1], 10) : 0;
  return hours * 3600 + mins * 60 + secs;
}
